import type { ApiScope } from '@salonomy/types';

/**
 * Plain-English labels + groupings for the PAT scope picker. The vocabulary is owned by the API
 * (entities/auth.ts ApiScope / SCOPES); this is presentation only. `read` and `write` are the two
 * coarse super-scopes — `read` ⊇ every `*:read`, `write` ⊇ every `*:write` AND every read — so the
 * picker offers them as a quick-start at the top and explains the containment, exactly as the
 * server's core/scopes.ts enforces it.
 */

export const SCOPE_LABEL: Record<ApiScope, string> = {
  read: 'Read everything',
  write: 'Read & write everything',
  'clients:read': 'Clients — read',
  'clients:write': 'Clients — write',
  'appointments:read': 'Appointments — read',
  'appointments:write': 'Appointments — write',
  'catalog:read': 'Services & catalog — read',
  'catalog:write': 'Services & catalog — write',
  'queue:read': 'Walk-in queue — read',
  'queue:write': 'Walk-in queue — write',
  'payments:read': 'Payments — read',
  'payments:write': 'Payments — write',
  'comms:read': 'Comms log — read',
  'comms:send': 'Comms — send messages',
  'stock:read': 'Stock — read',
  'stock:write': 'Stock — write',
  'reports:read': 'Reports — read',
  'webhooks:manage': 'Webhooks — manage',
};

/** The two coarse super-scopes, surfaced as quick-start presets above the fine-grained list. */
export const SUPER_SCOPES: ApiScope[] = ['read', 'write'];

/** Fine-grained scopes, in a sensible reading order for the picker. */
export const FINE_SCOPES: ApiScope[] = [
  'clients:read',
  'clients:write',
  'appointments:read',
  'appointments:write',
  'catalog:read',
  'catalog:write',
  'queue:read',
  'queue:write',
  'payments:read',
  'payments:write',
  'comms:read',
  'comms:send',
  'stock:read',
  'stock:write',
  'reports:read',
  'webhooks:manage',
];

/**
 * Mirror of the server's scope-containment logic (core/scopes.ts) for UX only: is a fine-grained
 * scope already covered by a chosen super-scope, so the picker can grey it out? `write` ⊇ everything;
 * `read` ⊇ every `*:read`. Kept simple and in step with the documented containment rule.
 */
export function isCoveredBySuper(scope: ApiScope, chosen: ReadonlySet<string>): boolean {
  if (scope === 'read' || scope === 'write') return false;
  if (chosen.has('write')) return true;
  if (chosen.has('read') && scope.endsWith(':read')) return true;
  return false;
}
